/**
 * Generates a credit card object with random details.
 * @param {string} [name=''] Name associated with the card.
 * @returns {{ cvc: string; name: string; expiry: string; cardNumber: string; }}
 * @example
 * createMestoreCard('John Doe')
 * // { cvc: '123', name: 'John Doe', expiry: '12/2023', cardNumber: '5241 3461 6789 0123' }
 */
export const createMestoreCard = (name = '') => {
  const prefix = Math.floor(Math.random() * 5) + 51;

  let cardNumber = prefix.toString();

  while (cardNumber.length < 15) {
    cardNumber += Math.floor(Math.random() * 10).toString();
  }

  const calculateLuhnCheckDigit = (number) => {
    let sum = 0;
    let shouldDouble = false;
    let index = number.length - 1;

    while (index >= 0) {
      let digit = Number(number[index]);

      if (shouldDouble) digit = digit * 2 > 9 ? digit * 2 - 9 : digit * 2;

      sum += digit;
      shouldDouble = !shouldDouble;
      index -= 1;
    }

    return (10 - (sum % 10)) % 10;
  };

  const lastDigit = calculateLuhnCheckDigit(cardNumber);

  cardNumber += lastDigit;

  const cvc = String(Math.floor(Math.random() * 900) + 100);
  const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
  const year = String(new Date().getFullYear() + Math.floor(Math.random() * 5));

  return {
    cvc,
    name,
    expiry: `${month}/${year}`,
    cardNumber: cardNumber.replace(/(\d{4})(?=\d)/g, '$1 '),
  };
};

/**
 * Remove spaces from a string value.
 * @param {string} inputValue The string to remove spaces.
 * @example removeSpaces('1234 5674 8766 5678') // '1234567487665678'
 */
export const removeSpaces = (inputValue) => inputValue.replace(/\s+/g, '');

/**
 * Return the last 8 digits of a card number.
 * @param {string} cardNumber The card number to get bin.
 * @example getBin('1234 5674 8766 5678') // '87665678'
 */
export const getBin = (cardNumber) => removeSpaces(cardNumber).substring(0, 8);

/**
 * Obfuscates all but the last 4 digits of a card number.
 * @param {string} cardNumber The card number to obfuscate.
 * @example obfuscateCardNumber('1234 5674 8766 5678') // '●●●● ●●●● ●●●● 5678'
 */
export const obfuscateCardNumber = (cardNumber, replaceWith = '●') => {
  if (!cardNumber) return '';

  const parts = cardNumber.split(' ');
  const lastPart = parts.pop();
  const hiddenParts = parts.map((part) => replaceWith.repeat(part.length));

  return [...hiddenParts, lastPart].join(' ');
};

export const getAverageRating = ({
  rating_5 = 0,
  rating_4 = 0,
  rating_3 = 0,
  rating_2 = 0,
  rating_1 = 0,
}) => {
  const totalReviews = rating_5 + rating_4 + rating_3 + rating_2 + rating_1;
  const totalStars = rating_5 * 5 + rating_4 * 4 + rating_3 * 3 + rating_2 * 2 + rating_1;

  const totalRating =
    totalReviews === 0 ? 0 : Number((totalStars / totalReviews).toFixed(2).replace(/\.?0+$/, ''));

  return { totalRating, totalReviews };
};

export function getInventoryType(units) {
  if (units < 1) return 'out of stock';

  if (units <= 100) return 'low stock';

  return 'in stock';
}

export const trimString = (input, length = 60) =>
  input.length > length ? `${input.substring(0, length)}...` : input;
